import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Bot } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { navigateToAgent } from '@/utils/navigationUtils';
import type { Agent } from '@/types/Agents';
import type { Contact } from '@/types/Contact';
import ContactSheet from '@/components/page-components/project/agents/contacts/ContactSheet';
import { useSelector } from 'react-redux';
import { selectConversations } from '@/redux/conversationsSlice';
import { useTranslation } from 'react-i18next';

interface SearchResult {
  type: 'agent' | 'contact';
  id: number;
  name: string;
  subtitle: string;
  agentId: number;
  agentName: string;
}

interface AgentSearchProps {
  agents: Agent[];
  placeholder?: string;
  className?: string;
}

const AgentSearch: React.FC<AgentSearchProps> = ({
  agents,
  placeholder,
  className = '',
}) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Use translation for placeholder if not provided
  const searchPlaceholder = placeholder || t('common.search_placeholder');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false);

  // Get conversations for ContactSheet
  const conversations = useSelector(selectConversations);

  // Keep input focused when popover opens
  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      // Small delay to ensure popover is rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isSearchOpen]);

  // Search results for popover
  const searchResults = useMemo(() => {
    if (!searchTerm.trim() || !agents) return [];
    const lowerSearchTerm = searchTerm.toLowerCase();

    const agentResults: SearchResult[] = [];
    const contactResults: SearchResult[] = [];

    agents.forEach(agent => {
      // Add agent matches
      const sip = agent.subscription?.sip_number;
      if (
        agent.name?.toLowerCase().includes(lowerSearchTerm) ||
        agent.phone_number?.toLowerCase().includes(lowerSearchTerm) ||
        sip?.toLowerCase().includes(lowerSearchTerm)
      ) {
        agentResults.push({
          type: 'agent',
          id: agent.id || 0,
          name: agent.name,
          subtitle: agent.phone_number || sip || 'No contact info',
          agentId: agent.id || 0,
          agentName: agent.name,
        });
      }

      // Add contact matches
      if (agent.contacts) {
        agent.contacts.forEach(contact => {
          if (
            contact.name?.toLowerCase().includes(lowerSearchTerm) ||
            contact.caller_id?.toLowerCase().includes(lowerSearchTerm)
          ) {
            contactResults.push({
              type: 'contact',
              id: contact.id,
              name: contact.name,
              subtitle: contact.caller_id,
              agentId: agent.id || 0,
              agentName: agent.name,
            });
          }
        });
      }
    });

    // Always show agents first, then contacts
    const allResults = [...agentResults, ...contactResults];
    return allResults.slice(0, 8); // Limit to 8 results
  }, [searchTerm, agents]);

  const handleSearchResultClick = (result: SearchResult) => {
    if (result.type === 'agent') {
      // Navigate to agent page
      navigateToAgent(navigate, result.agentId);
      setIsSearchOpen(false);
      setSearchTerm('');
    } else {
      // For contacts, open ContactSheet
      const agent = agents.find(a => a.id === result.agentId);
      const contact = agent?.contacts?.find(c => c.id === result.id);

      if (contact) {
        setSelectedContact(contact);
        setIsContactSheetOpen(true);
        setIsSearchOpen(false);
      }
    }
  };

  const handleContactSheetClose = () => {
    setIsContactSheetOpen(false);
    setSelectedContact(null);
  };

  const handleContactSave = async (contactId: number, name: string) => {
    // This would typically update the contact in your backend
    // For now, we'll just close the sheet
    console.log('Saving contact:', contactId, name);
    handleContactSheetClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsSearchOpen(value.length > 0);
  };

  const handleInputFocus = () => {
    if (searchTerm.length > 0) {
      setIsSearchOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Don't close immediately to allow clicking on results
    // Close after a small delay if no interaction
    setTimeout(() => {
      setIsSearchOpen(false);
    }, 150);
  };

  const handlePopoverOpenChange = (open: boolean) => {
    setIsSearchOpen(open);
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <>
      <Popover open={isSearchOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <div
            className={`relative min-w-0 ${isMobile ? '' : 'sm:w-auto max-w-xs'} ${className}`}
          >
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 transform ${isMobile ? 'h-5 w-5' : 'h-4 w-4'} text-muted-foreground`}
            />
            <Input
              ref={inputRef}
              placeholder={searchPlaceholder}
              className={`w-full ${isMobile ? 'h-12 pl-11 pr-4 text-base' : 'pl-10 pr-4'} rounded-lg border border-input bg-card transition-colors focus:border-blue-500 dark:focus:border-blue-400`}
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className={`${isMobile ? 'mx-4 w-[calc(100vw-2rem)]' : 'w-80'} rounded-lg border border-border bg-card p-0`}
          align={isMobile ? 'start' : 'end'}
          onOpenAutoFocus={e => e.preventDefault()}
        >
          {searchResults.length > 0 ? (
            <div className="max-h-96 overflow-y-auto">
              {searchResults.map((result, index) => (
                <div
                  key={`${result.type}-${result.id}-${index}`}
                  className={`flex cursor-pointer items-center gap-3 border-b border-border transition-colors last:border-b-0 ${isMobile ? 'min-h-[56px] p-4' : 'min-h-[44px] p-3'} hover:bg-muted/40 active:bg-muted/60`}
                  onClick={() => handleSearchResultClick(result)}
                  onMouseDown={e => e.preventDefault()} // Prevent input blur
                >
                  <div className="flex-shrink-0">
                    {result.type === 'agent' ? (
                      <Bot
                        className={`${isMobile ? 'h-6 w-6' : 'h-5 w-5'} text-blue-500`}
                      />
                    ) : (
                      <User
                        className={`${isMobile ? 'h-6 w-6' : 'h-5 w-5'} text-green-500`}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`flex ${isMobile ? 'flex-col' : 'items-center'} gap-1 ${isMobile ? '' : 'gap-2'}`}
                    >
                      <p
                        className={`${isMobile ? 'text-base' : 'text-sm'} truncate font-medium text-foreground`}
                      >
                        {result.name || t('common.unnamed')}
                      </p>
                      {result.type === 'contact' && (
                        <p
                          className={`${isMobile ? 'text-sm' : 'text-sm'} truncate text-muted-foreground`}
                        >
                          {result.subtitle}
                        </p>
                      )}
                    </div>

                    {result.type === 'contact' && (
                      <p
                        className={`${isMobile ? 'mt-1 text-xs' : 'text-xs'} truncate text-muted-foreground`}
                      >
                        {t('common.search.agent_label', {
                          name: result.agentName,
                        })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : searchTerm.trim() ? (
            <div
              className={`${isMobile ? 'p-6' : 'p-4'} text-center text-muted-foreground`}
            >
              <Search
                className={`${isMobile ? 'h-10 w-10' : 'h-8 w-8'} mx-auto mb-3 text-muted-foreground`}
              />
              <p className={isMobile ? 'text-base mb-1' : ''}>
                {t('common.search.no_results')}
              </p>
              <p
                className={`${isMobile ? 'text-sm' : 'text-sm'} text-muted-foreground`}
              >
                {t('common.search.try_different')}
              </p>
            </div>
          ) : (
            <div
              className={`${isMobile ? 'p-6' : 'p-4'} text-center text-muted-foreground`}
            >
              <Search
                className={`${isMobile ? 'h-10 w-10' : 'h-8 w-8'} mx-auto mb-3 text-muted-foreground`}
              />
              <p className={`${isMobile ? 'text-base mb-1' : ''} font-medium`}>
                {t('common.search.start_typing')}
              </p>
              <p
                className={`${isMobile ? 'text-sm' : 'text-sm'} text-muted-foreground`}
              >
                {t('common.search_placeholder')}
              </p>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Contact Sheet for viewing contact details */}
      {selectedContact && (
        <ContactSheet
          contact={selectedContact}
          isOpen={isContactSheetOpen}
          onClose={handleContactSheetClose}
          onSave={handleContactSave}
          conversations={conversations || []}
          agentId={selectedContact.agent_id}
          context="contacts"
        />
      )}
    </>
  );
};

export default AgentSearch;
